import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { IS_EUROPE } from '../config/region';
import { t } from '../i18n';
import { CATEGORIES, acceptAll, rejectAll, saveConsent, getStoredConsent } from '../utils/consent';
import './CookieBanner.css';

/*
 * Cookie banner and preference centre.
 *
 * Layout follows what the Cookie Policy commits us to, and the shape is load-bearing rather than
 * cosmetic: "Reject All" is styled identically to "Accept All" because consent must be as easy to
 * refuse as to give, and every optional category starts switched **off** because pre-ticked boxes
 * are prohibited. Nothing non-essential loads until this is answered — see utils/consent.js.
 *
 * The russia build keeps the old single-button notice: it operates under Russian law, where the
 * granular EU regime does not apply, and the existing wording is what its Cookie consent page
 * describes.
 */

const CATEGORY_COPY = () => ({
  necessary: {
    title: t('Строго необходимые'),
    text: t('Обеспечивают работу сайта: вход в аккаунт, безопасность, сохранение сессии, защита от подделки запросов. Без них платформа не работает, поэтому их нельзя отключить.'),
  },
  functional: {
    title: t('Функциональные'),
    text: t('Запоминают ваш выбор — язык, настройки интерфейса и отображения. Улучшают удобство пользования сайтом.'),
  },
  analytics: {
    title: t('Аналитические'),
    text: t('Помогают понять, как посетители пользуются платформой: какие страницы открывают, сколько времени проводят, откуда приходят. Включают записи сеансов (PostHog, серверы в ЕС): они показывают ваши действия и всё, что отображается на экране, включая сообщения в диалогах с ИИ-персонажами и ответы. Вводимый текст скрывается.'),
  },
  marketing: {
    title: t('Маркетинговые'),
    text: t('Используются для показа релевантной рекламы, оценки эффективности кампаний и ретаргетинга. Могут устанавливаться третьими лицами.'),
  },
});

const RussianBanner = ({ onDone }) => {
  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'true');
    document.documentElement.classList.add('cookie-accepted');
    onDone();
  };

  return (
    <div className="cb-banner">
      <span className="cb-title">Cookie</span>
      <span className="cb-text">
        {t('Продолжая использовать сайт, вы')}{' '}
        <Link to="/cookies">{t('соглашаетесь')}</Link>{' '}
        {t('на использование файлов Cookie')}
      </span>
      <button className="cb-accept" onClick={handleAccept}>
        {t('Принять')}
      </button>
    </div>
  );
};

const PlotexBanner = ({ onDone, openInSettings }) => {
  const [managing, setManaging] = useState(!!openInSettings);
  const [selection, setSelection] = useState(
    /* Optional categories default to off. A stored decision only appears here when the visitor
       reopened the centre deliberately, in which case their own choices are what to show. */
    () => getStoredConsent() || { functional: false, analytics: false, marketing: false }
  );
  const copy = CATEGORY_COPY();

  const finish = (fn) => { fn(); onDone(); };
  const toggle = (key) => setSelection(s => ({ ...s, [key]: !s[key] }));

  if (!managing) {
    return (
      <div className="cb-banner cb-banner--eu">
        <div className="cb-body">
          <span className="cb-title">{t('Мы используем файлы cookie')}</span>
          <span className="cb-text">
            {t('Строго необходимые cookie обеспечивают работу сайта. Функциональные, аналитические и маркетинговые используются только с вашего согласия.')}{' '}
            <Link to="/cookies">{t('Политика cookie')}</Link>
          </span>
        </div>
        <div className="cb-actions">
          {/* Accept and Reject are deliberately identical in weight */}
          <button className="cb-btn cb-btn--primary" onClick={() => finish(acceptAll)}>
            {t('Принять все')}
          </button>
          <button className="cb-btn cb-btn--primary" onClick={() => finish(rejectAll)}>
            {t('Отклонить все')}
          </button>
          <button className="cb-btn cb-btn--ghost" onClick={() => setManaging(true)}>
            {t('Настроить')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cb-banner cb-banner--eu cb-banner--panel">
      {/* Reopened from the footer, the visitor may just be looking. Every button below commits a
          decision, so without this there is no way out that does not change their settings. On a
          first visit there is deliberately no dismiss: the choice has to be made. */}
      {openInSettings && (
        <button type="button" className="cb-close" onClick={onDone} aria-label={t('Закрыть')}>✕</button>
      )}
      <div className="cb-body">
        <span className="cb-title">{t('Настройки cookie')}</span>
        <span className="cb-text">
          {t('Выберите категории, использование которых вы разрешаете. Решение можно изменить в любой момент.')}
        </span>
      </div>

      <div className="cb-cats">
        <div className="cb-cat cb-cat--locked">
          <div className="cb-cat-head">
            <span className="cb-cat-title">{copy.necessary.title}</span>
            <span className="cb-cat-always">{t('Всегда включены')}</span>
          </div>
          <p className="cb-cat-text">{copy.necessary.text}</p>
        </div>

        {CATEGORIES.map((key) => (
          <div className="cb-cat" key={key}>
            <label className="cb-cat-head">
              <span className="cb-cat-title">{copy[key].title}</span>
              <input
                type="checkbox"
                className="cb-cat-toggle"
                checked={!!selection[key]}
                onChange={() => toggle(key)}
              />
            </label>
            <p className="cb-cat-text">{copy[key].text}</p>
          </div>
        ))}
      </div>

      <div className="cb-actions">
        <button className="cb-btn cb-btn--primary" onClick={() => finish(() => saveConsent(selection))}>
          {t('Сохранить выбор')}
        </button>
        <button className="cb-btn cb-btn--primary" onClick={() => finish(acceptAll)}>
          {t('Принять все')}
        </button>
        <button className="cb-btn cb-btn--primary" onClick={() => finish(rejectAll)}>
          {t('Отклонить все')}
        </button>
      </div>
    </div>
  );
};

const CookieBanner = ({ onAccept, openInSettings }) => {
  const onDone = () => { if (onAccept) onAccept(); };
  return IS_EUROPE
    ? <PlotexBanner onDone={onDone} openInSettings={openInSettings} />
    : <RussianBanner onDone={onDone} />;
};

export default CookieBanner;
